"use client";

import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { H } from "../hero-tokens";
import { ResolutionDiagram } from "./scroll-stage-diagram";
import { FlyWindows } from "./scroll-stage-windows";

/**
 * Window choreography (independent of the per-context scroll timing below). The
 * windows' authored data-fly schedule [AUTH_MIN..AUTH_MAX] is remapped into
 * [WIN_START..WIN_END]: they begin near the start and have fully cleared by the
 * headline-reveal point (~Pw 0.48), staggered across the whole stretch.
 */
const AUTH_MIN = 0.3;
const AUTH_MAX = 0.858;
const WIN_START = 0.05;
const WIN_END = 0.48;

/**
 * The animated 1080×1108 stage canvas — nine "chaos" fake-app windows fly toward
 * the center, scale down and fade as you scroll, revealing the hausverwalter.ai
 * hub-and-spoke resolution diagram; the "Mach daraus zwei." headline fades in
 * behind them. Driven by a `requestAnimationFrame` loop that reads THIS canvas's
 * own position relative to the viewport (a 1:1 port of the prototype's
 * `_initFly`). Shared by desktop (full size) and mobile (scaled down to fit).
 *
 * Two `timing` modes map scroll → Pw 0..1:
 *
 * - `"top"` (desktop): Pw=0 when the canvas top is at `startOffset`·vh; Pw 0..1
 *   spans `span` viewport-heights of scroll. Tied to a tall canvas (~1.4·vh), so
 *   the animation resolves roughly as you scroll through it.
 * - `"center"` (mobile): Pw is driven by the canvas's on-screen CENTER moving
 *   from `start`·vh down to `end`·vh. The scaled mobile canvas is much shorter
 *   than the viewport, so a top/vh-based ramp would finish before the stage is
 *   even comfortably in view (the chaos windows would already be gone, leaving an
 *   empty gap). Center-anchoring keeps the windows visible at rest while the
 *   stage sits in view and resolves them only as it scrolls upward — robust to
 *   the phone's viewport height and the scaled canvas height alike.
 *
 * When `prefers-reduced-motion` is set we skip the loop and render the resolved
 * end-state statically (windows faded out, diagram fully visible).
 */
type ScrollStageTiming =
  | {
      readonly mode: "top";
      readonly startOffset: number;
      readonly span: number;
    }
  | { readonly mode: "center"; readonly start: number; readonly end: number };

export function ScrollStageCanvas({
  timing,
  headlineScale = 1,
  compactDiagramShadow = false,
  resolutionBlockOffsetY = 0,
}: {
  readonly timing: ScrollStageTiming;
  /**
   * Multiplier for the in-canvas "Mach daraus zwei." headline font sizes. The
   * whole canvas is authored at 1080px and scaled to fit; on mobile that scale
   * (~0.34) would shrink the 50px headline to ~17px — far smaller than the
   * native mobile intro heading. Mobile passes ~1.8 so it lands back at ~30px.
   */
  readonly headlineScale?: number;
  /** Tighter hub-card shadow on the scaled mobile stage. */
  readonly compactDiagramShadow?: boolean;
  /** Extra canvas height + downward shift for the resolution block (mobile). */
  readonly resolutionBlockOffsetY?: number;
}): ReactNode {
  const tI18n = useTranslations("marketing.scrollStage");
  const stageRef = useRef<HTMLDivElement | null>(null);
  // Flatten to stable primitives so the rAF effect doesn't restart every render.
  const mode = timing.mode;
  const a = timing.mode === "top" ? timing.startOffset : timing.start;
  const b = timing.mode === "top" ? timing.span : timing.end;

  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const flyEls = Array.from(
      stage.querySelectorAll<HTMLElement>("[data-fly]"),
    );
    const fadeEls = Array.from(
      stage.querySelectorAll<HTMLElement>("[data-fade-in]"),
    );

    if (reduceMotion) {
      // Resolved end-state: chaos windows collapsed + faded, fade-in headline on.
      for (const el of flyEls) {
        el.style.transform = "scale(0.14)";
        el.style.opacity = "0";
      }
      for (const el of fadeEls) {
        el.style.opacity = "1";
      }
      return;
    }

    const c01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
    const ease = (t: number): number =>
      t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

    let raf = 0;
    const tick = (): void => {
      const r = stage.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      // Scroll-linked, no pin/lock. getBoundingClientRect returns the on-screen
      // (already-scaled) rect, so both modes work when a parent scales the canvas.
      // "center" uses the canvas's on-screen center (height-aware) → robust to the
      // short scaled mobile stage; "top" uses the canvas top (tall desktop stage).
      const Pw =
        mode === "center"
          ? c01((vh * a - (r.top + r.height / 2)) / (vh * (a - b)))
          : c01((vh * a - r.top) / (vh * b));

      for (const el of flyEls) {
        const raw = el.getAttribute("data-fly");
        if (raw === null) continue;
        const d = raw.split(",");
        const dx = Number(d[0]);
        const dy = Number(d[1]);
        const rs = Number(d[2]);
        const re = Number(d[3]);
        // Remap the authored schedule into [WIN_START..WIN_END]: the window
        // begins near the start and clears by the headline point, staggered.
        const rsAdj =
          WIN_START +
          ((rs - AUTH_MIN) / (AUTH_MAX - AUTH_MIN)) * (WIN_END - WIN_START);
        const reAdj =
          WIN_START +
          ((re - AUTH_MIN) / (AUTH_MAX - AUTH_MIN)) * (WIN_END - WIN_START);
        const p = ease(c01((Pw - rsAdj) / (reAdj - rsAdj)));
        el.style.transform = `translate(${(dx * p).toFixed(1)}px,${(dy * p).toFixed(1)}px) scale(${(1 - 0.86 * p).toFixed(3)})`;
        el.style.opacity =
          p < 0.18 ? "1" : String(Math.max(0, 1 - (p - 0.18) / 0.5).toFixed(3));
      }

      for (const el of fadeEls) {
        const raw = el.getAttribute("data-fade-in");
        if (raw === null) continue;
        const f = raw.split(",");
        el.style.opacity = String(
          c01((Pw - Number(f[0])) / (Number(f[1]) - Number(f[0]))).toFixed(3),
        );
      }

      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [mode, a, b]);

  return (
    <div
      ref={stageRef}
      style={{
        position: "relative",
        width: 1080,
        height: 1108 + resolutionBlockOffsetY,
      }}
    >
      {/* Dotted texture, masked to fade at the bottom. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(11,15,25,0.05) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          WebkitMaskImage:
            "linear-gradient(180deg,#000 0%,#000 70%,transparent 92%)",
          maskImage: "linear-gradient(180deg,#000 0%,#000 70%,transparent 92%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <FlyWindows />

      {/* Ambient verdigris glow behind the resolution diagram. Desktop uses a wide
          ellipse that bleeds past the canvas; on mobile that hard-clips at the
          ResponsiveScale edge and reads as a sheared drop-shadow on the left card. */}
      {compactDiagramShadow ? null : (
        <div
          style={{
            position: "absolute",
            left: -300,
            top: 658 + resolutionBlockOffsetY,
            width: 1100,
            height: 380,
            borderRadius: "50%",
            background:
              "radial-gradient(ellipse at center, rgba(0,128,112,0.08) 0%, transparent 64%)",
            zIndex: 40,
            pointerEvents: "none",
          }}
        />
      )}

      {/* Resolution diagram (revealed as windows collapse). */}
      <div
        data-fly-anchor="1"
        style={{
          position: "absolute",
          left: 0,
          top: 706 + resolutionBlockOffsetY,
          zIndex: 50,
          width: 1080,
        }}
      >
        <ResolutionDiagram
          compactShadow={compactDiagramShadow}
          captionScale={headlineScale}
        />
      </div>

      {/* Fade-in headline behind the windows ("Mach daraus zwei.").
          0.1→0.4: starts fading in EARLY so it is already visible WHILE the
          chaos windows are still flying over it, and is fully legible by the
          time they clear (it stays full through the landing point). */}
      <div
        data-fade-in="0.1,0.4"
        style={{
          position: "absolute",
          left: 0,
          top: 480 + resolutionBlockOffsetY,
          width: 1080,
          textAlign: "center",
          zIndex: 1,
          opacity: 0,
        }}
      >
        <h2
          style={{
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: 50 * headlineScale,
            lineHeight: 1.04,
            letterSpacing: "-0.02em",
            color: "#0B0F19",
            margin: 0,
          }}
        >
          {tI18n.rich("machDarausZwei", {
            em: (chunks) => (
              <em style={{ fontStyle: "italic", color: H.accent }}>{chunks}</em>
            ),
          })}
        </h2>
        <p
          style={{
            fontSize: 19 * headlineScale,
            lineHeight: 1.55,
            color: "rgba(11,15,25,0.6)",
            margin: `${18 * headlineScale}px auto 0`,
            maxWidth: 560 * headlineScale,
          }}
        >
          {tI18n("wenigerKostenWenigerReibung")}
        </p>
      </div>
    </div>
  );
}

/**
 * SECTION 4 — "Chaos → Ein System" (desktop). The centered intro heading
 * ("Acht Uhr. Schon zwölf Programme offen.") above the full-size animated
 * {@link ScrollStageCanvas}. The mobile hero renders the same canvas scaled
 * down (see `hero-mobile-stage.tsx`).
 */
export function ScrollStage(): ReactNode {
  const t = useTranslations("marketing.scrollStage");
  return (
    <div
      style={{
        position: "relative",
        margin: "130px auto 0",
        maxWidth: 1240,
        padding: "0 20px",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto" }}>
        <h2
          style={{
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: 50,
            lineHeight: 1.05,
            letterSpacing: "-0.018em",
            color: "#0B0F19",
            margin: 0,
          }}
        >
          {t.rich("achtUhrProgrammeOffen", {
            em: (chunks) => (
              <em style={{ fontStyle: "italic", color: H.accent }}>{chunks}</em>
            ),
          })}
        </h2>
        <p
          style={{
            fontSize: 18,
            lineHeight: 1.55,
            color: "rgba(11,15,25,0.6)",
            margin: "16px auto 0",
            maxWidth: 530,
          }}
        >
          {t("jederVorgangVerteiltMehrere")}
        </p>
      </div>

      <div style={{ width: 1080, margin: "54px auto 0" }}>
        <ScrollStageCanvas
          timing={{ mode: "top", startOffset: 0.5, span: 1.6 }}
        />
      </div>
    </div>
  );
}
